import React from 'react'
import { cn } from '@/lib/utils'
import Tick from '@/assets/builder/icon/Tick'

interface Option {
    id: string
    text: string
}

interface MultiSelectProps {
    questionId: string
    options: Option[]
    allowOther?: boolean
    otherLabel?: string
    selectedOptionIds?: string[]
    otherValue?: string
    maxSelect?: number | null
}

const MultiSelect: React.FC<MultiSelectProps> = ({
    questionId,
    options,
    allowOther = false,
    otherLabel = 'Other',
    selectedOptionIds = [],
    otherValue = '',
    maxSelect = null,
}) => {
    return (
        <div className="relative checkbox-group mt-4 flex flex-col gap-[14px]">
            {maxSelect && (
                <p className="text-[12px] font-[400] text-[#6B7280]">
                    Select up to {maxSelect}
                </p>
            )}
            {options.map((option: Option, index: number) => (
                <div
                    key={`preview-modal-option-${questionId}-${option.id}-${index}`}
                    className="checkbox-option"
                >
                    <CheckboxButton
                        key={`preview-modal-checkbox-${questionId}-${option.id}-${index}`}
                        id={`${questionId}-${option.id}`}
                        checked={selectedOptionIds.includes(option.id)}
                        label={
                            option?.text !== ''
                                ? option.text
                                : `Response ${index + 1}`
                        }
                    />
                </div>
            ))}

            {allowOther && (
                <div className="relative checkbox-option">
                    <CheckboxButton
                        key={`preview-modal-other-${questionId}-checkbox`}
                        id={`${questionId}-other`}
                        checked={selectedOptionIds.includes('other')}
                        label={otherLabel}
                    />
                    {selectedOptionIds.includes('other') && (
                        <input
                            key={`preview-modal-other-input-${questionId}-${otherValue}`}
                            type="text"
                            value={otherValue}
                            disabled
                            className={cn(
                                'mt-2 ml-7 w-[414px] outline-none rounded-md placeholder-[#616161] border border-[0.5px] border-[#8A8A8A] bg-background px-3 py-2 text-sm shadow-none',
                                otherValue !== '' ? 'font-[550]' : 'font-[400]'
                            )}
                            placeholder="Please specify"
                        />
                    )}
                </div>
            )}
        </div>
    )
}

interface CheckboxButtonProps {
    id: string
    checked: boolean
    label: string
}

const CheckboxButton: React.FC<CheckboxButtonProps> = ({
    id,
    checked,
    label,
}) => {
    return (
        <label
            htmlFor={`${id}-preview`}
            key={`preview-modal-label-${id}-${checked}`}
            className="flex items-center text-[14px] font-[400] select-none gap-[11px]"
        >
            <div className="relative flex-shrink-0 w-[18px] h-[18px]">
                <input
                    type="checkbox"
                    id={`${id}-preview`}
                    key={`preview-modal-input-${id}-${checked}`}
                    checked={checked}
                    disabled
                    className="sr-only"
                />
                <span
                    className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-[4px] transition-colors ${checked ? 'bg-[#015AD1]' : 'border border-[#DEDEDE]'
                        }`}
                >
                    {checked && <Tick />}
                </span>
            </div>
            <span>{label}</span>
        </label>
    )
}

export default MultiSelect
